/**
 * InfinitSales - Create Notification Modal
 * Modal simples para o operador criar uma notificacao interna individual,
 * opcionalmente ligada ao contato atual (chamado pelo menu fixo do chat).
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Bell, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const CreateNotificationModal = ({ isOpen, onClose, onSubmit, contactName }) => {
  const { colors } = useTheme();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setBody('');
      setScheduledAt('');
      setSaving(false);
      setError(null);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      // datetime-local é hora local; getTime() dá epoch ms (UTC) — sem ambiguidade.
      const scheduled_at = scheduledAt ? new Date(scheduledAt).getTime() : null;
      await onSubmit({ title: title.trim(), body: body.trim(), scheduled_at });
      onClose();
    } catch (e) {
      setError('Não foi possível criar a notificação.');
      setSaving(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 1200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
    },
    modal: {
      width: 420,
      maxWidth: '100%',
      backgroundColor: colors.bgPrimary,
      borderRadius: 14,
      border: `1px solid ${colors.borderColor}`,
      boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 16px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    iconWrap: {
      width: 34,
      height: 34,
      borderRadius: 9,
      backgroundColor: `${colors.accentPrimary}18`,
      color: colors.accentPrimary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    headerText: { flex: 1, minWidth: 0 },
    headerTitle: { fontSize: 15, fontWeight: 600, color: colors.textPrimary },
    headerSub: { fontSize: 12, color: colors.textSecondary },
    closeBtn: {
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      padding: 4,
    },
    body: { padding: 16, display: 'flex', flexDirection: 'column', gap: 12 },
    label: { fontSize: 12, fontWeight: 600, color: colors.textSecondary },
    input: {
      width: '100%',
      border: `1px solid ${colors.inputBorder}`,
      backgroundColor: colors.bgTertiary,
      borderRadius: 10,
      padding: '10px 12px',
      fontSize: 14,
      color: colors.textPrimary,
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: 80,
      resize: 'vertical',
      border: `1px solid ${colors.inputBorder}`,
      backgroundColor: colors.bgTertiary,
      borderRadius: 10,
      padding: '10px 12px',
      fontSize: 14,
      color: colors.textPrimary,
      outline: 'none',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    error: { fontSize: 12, color: colors.danger || '#ef4444' },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 8,
      padding: '12px 16px',
      borderTop: `1px solid ${colors.borderColor}`,
    },
    cancelBtn: {
      padding: '8px 14px',
      borderRadius: 8,
      border: `1px solid ${colors.borderColor}`,
      backgroundColor: 'transparent',
      color: colors.textSecondary,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
    },
    submitBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '8px 16px',
      borderRadius: 8,
      border: 'none',
      backgroundColor: colors.accentPrimary,
      color: '#fff',
      fontSize: 13,
      fontWeight: 600,
      cursor: title.trim() && !saving ? 'pointer' : 'not-allowed',
      opacity: title.trim() && !saving ? 1 : 0.6,
    },
  };

  return createPortal(
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.iconWrap}><Bell size={18} /></div>
          <div style={styles.headerText}>
            <div style={styles.headerTitle}>Criar notificação</div>
            {contactName && (
              <div style={styles.headerSub}>Sobre: {contactName}</div>
            )}
          </div>
          <button style={styles.closeBtn} onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div style={styles.body}>
          <div>
            <div style={styles.label}>Título</div>
            <input
              ref={inputRef}
              style={styles.input}
              value={title}
              maxLength={200}
              placeholder="Ex.: Retornar ligação amanhã"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
              }}
            />
          </div>
          <div>
            <div style={styles.label}>Detalhes (opcional)</div>
            <textarea
              style={styles.textarea}
              value={body}
              maxLength={2000}
              placeholder="Anote um lembrete sobre este contato…"
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div>
            <div style={styles.label}>Agendar para (opcional)</div>
            <input
              type="datetime-local"
              style={styles.input}
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            <div style={{ fontSize: 11, color: colors.textTertiary, marginTop: 4 }}>
              Se preenchido, a notificação aparece como toast no horário escolhido.
            </div>
          </div>
          {error && <div style={styles.error}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>Cancelar</button>
          <button style={styles.submitBtn} onClick={handleSubmit} disabled={!title.trim() || saving}>
            {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
            {scheduledAt ? 'Agendar' : 'Criar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateNotificationModal;
